import DateFnsUtils from '@date-io/date-fns';
import { TextFieldProps } from '@material-ui/core/TextField';
import {
  KeyboardTimePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { FieldTitle, InputHelperText, InputProps, useInput } from 'react-admin';

import sanitizeRestProps from './sanitizeRestProps';
import { WrapperVariant } from '@material-ui/pickers/wrappers/Wrapper';

/**
 * Convert Date object to String
 *
 * @param {Date} value value to convert
 * @returns {String} A standardized date (yyyy-MM-dd), to be passed to an <input type="date" />
 */
const convertDateToString = (value: Date) => {
  if (!(value instanceof Date) || isNaN(value.getDate())) return;
  const pad = '00';
  const yyyy = value.getFullYear().toString();
  const MM = (value.getMonth() + 1).toString();
  const dd = value.getDate().toString();
  return `${yyyy}-${(pad + MM).slice(-2)}-${(pad + dd).slice(-2)}`;
};

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
const timeRegex = /^\d{2}:\d{2}$/;

const getStringFromDate = (value: string | Date) => {
  // null, undefined and empty string values should not go through dateFormatter
  // otherwise, it returns undefined and will make the input an uncontrolled one.
  if (value == null || value === '') {
    return '';
  }

  if (value instanceof Date) {
    return convertDateToString(value);
  }

  // valid dates should not be converted
  if (dateRegex.test(value)) {
    return value;
  }

  // if time HH:mm format (ex: 10:20) then convert to Date
  if (timeRegex.test(value)) {
    const parts = value.split(':');
    const date = new Date();
    return date.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10));
  }

  return convertDateToString(new Date(value));
};

// return time format HH:mm as value
const getTimeFromDate = (value: Date) => {
  if (value instanceof Date) {
    return moment(value).format('HH:mm');
  }
  return '';
};
export const TimeInput = (
  props: InputProps<TextFieldProps> &
    Omit<TextFieldProps, 'helperText' | 'label'>
) => {
  const {
    format = getStringFromDate,
    label,
    options,
    source,
    resource,
    helperText,
    margin = 'dense',
    meta,
    onBlur,
    onFocus,
    onChange,
    parse = getTimeFromDate,
    validate,
    variant = 'inline',
    autoOk = false,
    disableToolbar = true,
    ampm = false,
    ...rest
  } = props;

  const {
    id,
    input,
    isRequired,
    meta: { error, touched }
  } = useInput({
    format,
    onBlur,
    onFocus,
    onChange,
    parse,
    resource,
    source,
    validate,
    ...rest
  });

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <KeyboardTimePicker
        id={id}
        {...input}
        disableToolbar={disableToolbar}
        variant={variant as WrapperVariant}
        margin={margin}
        label={
          <FieldTitle
            label={label}
            source={source}
            resource={resource}
            isRequired={isRequired}
          />
        }
        error={!!(touched && error)}
        value={input.value ? input.value : null}
        helperText={
          error || helperText
            ? React.createElement(InputHelperText, {
                touched: touched,
                error: error,
                helperText: helperText
              })
            : null
        }
        InputProps={{
          autoComplete: 'off'
        }}
        autoOk={autoOk}
        ampm={ampm}
        {...sanitizeRestProps(rest as any)}
      />
    </MuiPickersUtilsProvider>
  );
};

TimeInput.propTypes = {
  label: PropTypes.string,
  options: PropTypes.object,
  resource: PropTypes.string,
  source: PropTypes.string
};

TimeInput.defaultProps = {
  options: {}
};

export default TimeInput;
